import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  useChatScopes,
  useChatStarters,
  useChatThread,
  useChatThreads,
  useClock,
  useDeleteChatThread,
  useSendChatMessage,
  type ChatScope,
  type ChatScopeId,
} from '../lib/data'
import { Composer, type ComposerDraft, type ChatMode } from '../components/chat/Composer'
import { EmptyThread } from '../components/chat/EmptyThread'
import { Message, PendingTurn } from '../components/chat/Message'
import { ThreadHeader } from '../components/chat/ThreadHeader'
import { ThreadList } from '../components/chat/ThreadList'
import { messageCountLine } from '../components/chat/labels'
import { Unavailable } from '../components/interests/parts'

// ============================================================================
// CHAT — docs/design/handoff/"LifeOS Chat.dc.html", options 11a (active thread)
// and 11b (empty thread).
//
// PURPOSE (README §10): one conversational engine over the user's whole life.
// LAYOUT: rail 62 · thread list 262 · conversation (flexible).
//
// WHAT IS REAL HERE, on demo data: the thread list and its grouping, thread
// selection, the whole exemplar conversation with every source card resolved
// against a real entity and linking to its real route, scope switching (which
// re-derives the size line under it), the Widen action on the scope-limit strip,
// thread search, and the empty state with its derived starter counts.
//
// SENDING IS REAL SINCE V4 in remote mode: the composer posts to the one chat
// engine, which retrieves first and answers only from what it read. In demo mode
// it refuses with a sentence saying why — there is no model behind that dataset,
// and writing an answer by hand would mean writing the citations too. Rename and
// Save to journal are still unbuilt and still say so.
//
// SELECTION LIVES IN THE URL (`?thread=`), the precedent the Interests screen
// set with `?cluster=` / `?save=`: it makes one thread linkable and one screen
// state photographable, which is what the port-proof shots need. `?thread=new`
// is the unsent thread — 11b — and so is NO `?thread=` at all (owner, 2026-09-11:
// "make the chat page open to a new chat by default when the app is reopened").
// The mockup's own default was the newest thread (11a); in use that meant every
// visit to Chat from the rail landed in whatever was said last, and a new
// question got typed under an old conversation. The list on the left is still
// one click from any earlier thread.
//
// SCOPE IS PER-THREAD STATE (README §Interactions → Chat) and it lives in a map
// keyed by thread id rather than one variable, because switching threads must not
// carry the scope you set on the last one into a conversation that never had it.
//
// OTHER SCREENS CAN ASK. `?ask=<question>&scope=<scopeId>&send=1` opens a new
// thread carrying a question the owner typed somewhere else — today that is the
// lecture page's ask bar, and the extension point exists so the next one does not
// grow a second chat client. The three parameters are CONSUMED and then removed
// from the URL: they describe an arrival, not a state, and a `?ask=` left in the
// address bar would re-ask the question on every reload.
// ============================================================================

/** The unsent thread. A sentinel in the URL rather than a separate route — 11b is a state. */
const NEW_THREAD = 'new'

/** Is this a scope this app has? A `?scope=` from a stale link must not become a scope chip. */
function asScopeId(value: string | null, scopes: ChatScope[] | undefined): ChatScopeId | null {
  if (!value) return null
  if (value.startsWith('course:')) {
    // A course scope is only real if that course is. An id from a dropped course would otherwise
    // render a chip with no label and answer from a scope the Worker resolves to nothing.
    return scopes?.some((s) => s.id === value) ? (value as ChatScopeId) : null
  }
  return (['everything', 'journal', 'school', 'interests', 'learning'] as const).includes(
    value as never,
  )
    ? (value as ChatScopeId)
    : null
}

export function Chat() {
  const [params, setParams] = useSearchParams()
  const [scopeOverrides, setScopeOverrides] = useState<Record<string, ChatScopeId>>({})
  const [newScope, setNewScope] = useState<ChatScopeId>('everything')
  const [draft, setDraft] = useState<ComposerDraft | null>(null)
  /**
   * THE QUESTION BEING ANSWERED, drawn in the conversation until the answer is there to replace
   * it. Local state rather than the mutation's own: it must outlive `isPending` by the one render
   * it takes a NEW thread's URL to follow the answer, or the starters would flash in between.
   */
  const [asking, setAsking] = useState<{ text: string; mode: ChatMode } | null>(null)
  /** Monotonic, so the same question asked twice is still two arrivals. */
  const drafts = useRef(0)

  const { data: clock } = useClock()
  const threads = useChatThreads()
  const scopes = useChatScopes()
  const starters = useChatStarters()
  const send = useSendChatMessage()
  const remove = useDeleteChatThread()

  const requested = params.get('thread')
  // With no `?thread=`, a NEW thread opens. See the header: the mockup defaulted to the newest
  // thread, and the owner asked for the blank one — a question typed on arrival should start a
  // conversation, not continue whichever one happened to be last.
  const isNewThread = requested === null || requested === NEW_THREAD
  const activeId = isNewThread ? undefined : requested
  const detail = useChatThread(activeId)

  const thread = detail.data?.thread
  const activeScopeId: ChatScopeId = isNewThread
    ? newScope
    : (thread && (scopeOverrides[thread.id] ?? thread.scopeId)) || 'everything'
  const activeScope = scopes.data?.find((s) => s.id === activeScopeId)

  function select(thread: string | null) {
    const merged = new URLSearchParams(params)
    if (thread === null) merged.delete('thread')
    else merged.set('thread', thread)
    setParams(merged, { replace: true })
  }

  function setScope(id: ChatScopeId) {
    if (isNewThread) setNewScope(id)
    else if (thread) setScopeOverrides((prev) => ({ ...prev, [thread.id]: id }))
  }

  // A QUESTION THAT ARRIVED FROM ANOTHER SCREEN. It waits for the scope list, because a
  // `course:` scope cannot be checked against the corpus before the corpus has answered, and a
  // question sent at the wrong scope is a question answered out of the wrong half of a life.
  const askParam = params.get('ask')
  const scopeParam = params.get('scope')
  const sendParam = params.get('send')
  const scopesData = scopes.data
  const scopesPending = scopes.isPending
  useEffect(() => {
    if (askParam === null && scopeParam === null) return
    if (scopesPending) return
    const scopeId = asScopeId(scopeParam, scopesData)
    if (scopeId) setNewScope(scopeId)
    if (askParam) {
      drafts.current += 1
      setDraft({
        text: askParam,
        // A SCOPE THAT DID NOT RESOLVE STOPS THE SEND, and only the send: the question still
        // lands in the field so nothing typed is lost, but the app does not answer it out of
        // "Everything" while the link said otherwise.
        send: sendParam === '1' && (scopeParam === null || scopeId !== null),
        key: drafts.current,
      })
    }
    // The parameters described an arrival, not a state. Left in place, a reload would re-ask.
    const merged = new URLSearchParams(params)
    merged.delete('ask')
    merged.delete('scope')
    merged.delete('send')
    setParams(merged, { replace: true })
  }, [askParam, scopeParam, sendParam, scopesPending, scopesData, params, setParams])

  return (
    <div className="flex min-h-0 flex-1">
      <ThreadList
        threads={threads.data}
        today={clock?.date}
        activeThreadId={activeId}
        isNewThread={isNewThread}
        isPending={threads.isPending}
        error={threads.error}
        onSelect={(id) => select(id)}
        onNew={() => select(NEW_THREAD)}
        // THE URL FOLLOWS THE DELETE, and only when it has to. Removing the thread that is open
        // would otherwise leave `?thread=<gone>` in the address bar and the conversation column
        // rendering "there is no thread with that id" — a not-found state for something the owner
        // just chose to remove, which reads as a bug rather than as the act succeeding. Clearing
        // the parameter falls back to the new thread, the screen's own default. Deleting a
        // thread that is NOT open leaves the URL alone: the conversation on screen did not move.
        onDelete={(id) => {
          if (id === activeId) select(null)
          remove.mutate(id)
        }}
        // A DELETE THAT FAILED IS SHOWN, not swallowed. Without this the list simply refetches
        // itself unchanged and the row is still there — which looks like a menu that does nothing.
        deleteError={remove.error}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <ThreadHeader
          title={isNewThread ? null : (thread?.title ?? null)}
          startedAt={thread?.startedAt ?? null}
          scopes={scopes.data}
          activeScopeId={activeScopeId}
          onScope={setScope}
        />

        {isNewThread && !asking ? (
          <EmptyThread
            starters={starters.data}
            isPending={starters.isPending}
            error={starters.error}
            onStart={(starter) => {
              // A STARTER SETS THE SCOPE **AND** CARRIES ITS QUESTION DOWN TO THE COMPOSER,
              // which is what the card promised by printing the question on itself. It does not
              // send it: the sentence on the card is the app's phrasing, not the owner's, and
              // "What did I save about memory?" is a starting point he is meant to edit. Landing
              // it in a focused field costs one keystroke and keeps the send a deliberate act —
              // the same reason the composer never sends what it merely holds.
              setNewScope(starter.scopeId)
              drafts.current += 1
              setDraft({ text: starter.prompt, send: false, key: drafts.current })
            }}
          />
        ) : (
          <Conversation
            detail={isNewThread ? undefined : detail.data}
            isPending={detail.isPending}
            error={detail.error}
            onWiden={setScope}
            asking={asking}
          />
        )}

        <Composer
          scope={activeScope}
          placeholder={
            activeScopeId.startsWith('course:')
              ? 'Ask about this course…'
              : 'Ask anything about your life…'
          }
          showExplainer={!isNewThread}
          isPending={send.isPending}
          error={send.error}
          note={send.data?.note ?? null}
          draft={draft}
          onSend={async (text, mode, attachmentIds) => {
            setAsking({ text, mode })
            try {
              const result = await send.mutateAsync({
                // `?thread=new` is the unsent thread, and the engine creates one for a null id.
                threadId: isNewThread ? null : (activeId ?? null),
                text,
                scopeId: activeScopeId,
                tier: mode,
                attachmentIds,
              })
              // The answer lands in a thread, so the URL follows it there — otherwise the reply to
              // the question just asked would be one the screen is not showing.
              select(result.thread.id)
              return result
            } finally {
              setAsking(null)
            }
          }}
        />
      </div>
    </div>
  )
}

/** One shared empty set, so "nothing is streaming" never allocates. */
const EMPTY_IDS: ReadonlySet<string> = new Set<string>()

function Conversation({
  detail,
  isPending,
  error,
  onWiden,
  asking,
}: {
  detail: ReturnType<typeof useChatThread>['data']
  isPending: boolean
  error: unknown
  onWiden: (id: ChatScopeId) => void
  /** The question on its way, drawn after the thread's messages until its answer replaces it. */
  asking: { text: string; mode: ChatMode } | null
}) {
  const end = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (asking) end.current?.scrollIntoView({ block: 'end' })
  }, [asking])

  // WHICH MESSAGES ARE NEW. Every id this component has already painted is remembered; anything
  // that turns up afterwards arrived in front of the owner and gets 13e's reveal. Kept in a ref
  // rather than state on purpose — it must not itself cause a render, or the first paint of a new
  // answer would happen before the set knew the answer was new.
  const seen = useRef<{ threadId: string; ids: Set<string>; streamed: Set<string> } | null>(null)
  let arrived: ReadonlySet<string> = EMPTY_IDS
  if (detail) {
    const threadId = detail.thread.id
    if (seen.current === null || seen.current.threadId !== threadId) {
      // A DIFFERENT THREAD IS NOT NEW MAIL. Switching threads paints a conversation that already
      // existed, so its whole history counts as seen and none of it streams.
      seen.current = {
        threadId,
        ids: new Set(detail.messages.map((m) => m.id)),
        streamed: new Set(),
      }
    } else {
      for (const m of detail.messages) {
        if (seen.current.ids.has(m.id)) continue
        seen.current.ids.add(m.id)
        if (m.role === 'assistant') seen.current.streamed.add(m.id)
      }
    }
    // Kept, not recomputed: a re-render triggered by anything else mid-stream must not flip the
    // prop back to false and snap a half-revealed answer into place.
    arrived = seen.current.streamed
  }

  // A FIRST QUESTION HAS NO THREAD YET — the engine makes one when it answers. Until then the
  // conversation is that one question and the wait under it.
  if (!detail && asking) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[30px] pt-5">
        <PendingTurn text={asking.text} mode={asking.mode} />
        <div ref={end} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-0 flex-1 overflow-y-auto px-[30px] pt-5">
        <Unavailable error={error} className="max-w-[760px]" />
      </div>
    )
  }

  if (isPending) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4 px-[30px] pt-5" aria-hidden>
        {[80, 160, 80, 200].map((h, i) => (
          <div key={i} className="animate-pulse rounded-card bg-surface-4" style={{ height: h }} />
        ))}
        <span className="sr-only">Loading…</span>
      </div>
    )
  }

  if (!detail) {
    return (
      <Centered>
        There is no thread with that id. It may have been deleted, or the link may be from another
        dataset. The list on the left is every thread there is.
      </Centered>
    )
  }

  // A THREAD WHOSE MESSAGES ARE NOT HERE. The demo dataset ships one fully written conversation;
  // the other six carry their length and nothing else, because writing them would mean writing
  // forty-two answers with invented citations — the one thing this screen exists to prevent. So
  // the screen says that in words rather than rendering an empty conversation, which would claim
  // the thread is empty when its own row says it holds nine messages.
  if (detail.messages.length === 0) {
    return (
      <Centered>
        This thread holds {messageCountLine(detail.thread.messageCount)} and none of them came
        back. The demo dataset ships one conversation in full — “Midterm prep — COGSCI 175” — and
        does not invent the rest, because every answer here has to carry the entries and
        transcripts it read. The threads, their scopes and the scope sizes above are all real.
      </Centered>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-[30px] pt-5">
      {detail.messages.map((message) => (
        <Message
          key={message.id}
          message={message}
          onWiden={onWiden}
          // Which thread to re-read after Accept or Reject. A proposal card changes the thread's
          // own contents (the card's status) as well as the calendar, so both get invalidated.
          threadId={detail.thread.id}
          // 13e streams an answer that arrived WHILE YOU WATCHED, and nothing else. Opening a
          // thread and re-playing nine old answers as if they were being written would be the
          // screen pretending; scrolling back through history is reading, not waiting.
          stream={arrived.has(message.id)}
        />
      ))}
      {asking && <PendingTurn text={asking.text} mode={asking.mode} />}
      <div ref={end} />
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center px-[30px]">
      <p className="max-w-[520px] text-center text-[12px] leading-[1.55] text-microcopy text-pretty">
        {children}
      </p>
    </div>
  )
}
